import { format } from 'date-fns';
import { groupBy, pick, isEmpty, trim, last } from 'lodash-es';
import { formatDate, getCleanedUrlParams, getFullName } from '@utils';
import {
  TRADE_TICKETS_CONTRACT_PERIODS,
  TRADE_TICKETS_STAGES_NAMES,
  TRADE_TICKETS_STAGES_ORDER,
  TRADE_TICKETS_PAGE_SIZE,
} from 'components/tradeTickets/constants';
import { URLS } from 'layouts/DrawerLayout/constants';
import { FILTER_OPERATOR_ENUMS, FILTER_RULE_ENUMS } from '@constants';

const CHANGELOG_ACTIONS_MAP = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

export const CUSTOMER_PREFERRED_PRICE = 'Customer Preferred Price';

const handleTradeTicketsData = (data, assignees, getAssigneeName) => {
  const assigneeMap = new Map(assignees.map((a) => [a.email, a]));

  return data.map((item) => {
    const fullName =
      getFullName(item.userInformation) ||
      getFullName(assigneeMap.get(item.userInformation?.email)) ||
      '-';
    const selectedVehicleForLeasing = item.vehiclesForLeasing?.find(
      (vehicle) => vehicle.isSelected
    );
    const usedVehicle =
      item?.vehicleForSale?.vehicleDetails?.manufacturer &&
      item?.vehicleForSale?.vehicleDetails?.model
        ? `${item?.vehicleForSale?.vehicleDetails?.manufacturer} ${item?.vehicleForSale?.vehicleDetails?.model} ${item?.vehicleForSale?.vehicleDetails?.edition}`
        : '-';
    const vehicle =
      selectedVehicleForLeasing?.vehicleComponent?.manufacturer &&
      selectedVehicleForLeasing?.vehicleComponent?.model
        ? `${selectedVehicleForLeasing?.vehicleComponent?.manufacturer} ${selectedVehicleForLeasing?.vehicleComponent?.model} ${selectedVehicleForLeasing?.vehicleComponent?.edition}`
        : '-';
    const vehicleId = selectedVehicleForLeasing?.vehicleComponent?.skuId || '';
    const usedVehicleLicencePlate =
      item?.vehicleForSale?.vehicleDetails?.plate || '-';
    const contractPeriod =
      selectedVehicleForLeasing?.contractPeriod &&
      TRADE_TICKETS_CONTRACT_PERIODS[
        selectedVehicleForLeasing?.contractPeriod
      ] &&
      selectedVehicleForLeasing?.contractPeriod;

    return {
      id: item.id,
      internalId: item.internalId,
      stage: item?.ticketDetails?.stage,
      fullName,
      usedVehicle,
      vehicle,
      vehicleId,
      assignee: getAssigneeName(item?.ticketDetails?.assignee),
      usedVehicleLicencePlate,
      contractPeriod,
      createdAt: item?.createdAt || '-',
      href: item?.id ? `/tradeTickets/${item.id}` : '',
      date: item?.createdAt
        ? format(new Date(item.createdAt), 'dd MMMM yyyy')
        : '-',
      cardContent: [
        {
          title: 'Client',
          value: fullName || '-',
        },
        {
          title: 'Sell',
          value: usedVehicle || '-',
        },
        {
          title: 'Plate',
          value: usedVehicleLicencePlate,
        },
        {
          title: 'Lease',
          value: vehicle,
          url: `${URLS.catalogCars}/${vehicleId}/edit`,
        },
        {
          title: 'Contract Period',
          value: contractPeriod,
        },
      ],
      customerEmail: item.userInformation?.email || '-',
      note: item?.ticketDetails?.note || '-',
      hasRequiredFiles: item?.hasRequiredFiles,
    };
  });
};

const getTradeTicketsColumns = (tradeTickets = []) => {
  const groupedByStage = groupBy(tradeTickets, 'stage');

  const columns = TRADE_TICKETS_STAGES_ORDER.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: TRADE_TICKETS_STAGES_NAMES[cur].label,
      cards: groupedByStage[cur] || [],
      styles: TRADE_TICKETS_STAGES_NAMES[cur].styles,
      value: true,
      label: TRADE_TICKETS_STAGES_NAMES[cur].label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

const getTradeTicketsQueryParams = (payload = {}) => {
  if (!payload?.pageSize) {
    payload.pageSize = TRADE_TICKETS_PAGE_SIZE;
  }
  if (!payload?.page) {
    payload.page = '1';
  }

  return pick(payload, [
    'page',
    'pageSize',
    'query',
    'orderBy',
    'order',
    'hasRequiredFiles',
  ]);
};

const getTradeTicketsPayload = (payload) => {
  const numberFields = [];
  const multiFilterNumberFields = ['stage', 'contractPeriod'];
  const multiFilterStringFields = ['assignee'];

  const fields = pick(payload, ['stage', 'contractPeriod', 'assignee']);

  const fieldsMap = {
    stage: 'ticketDetails.stage',
    contractPeriod: 'ticketDetails.selectedContractPeriod',
    assignee: 'ticketDetails.assignee',
    dateFrom: 'createdAt',
    dateTo: 'createdAt',
  };

  const { dateFrom, dateTo } = payload;

  const _getValue = ({ field }) => {
    if (multiFilterStringFields.includes(field)) {
      return payload[field]
        ?.split(',')
        .map((item) => (item === 'none' ? '' : item));
    }
    if (multiFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }

    return numberFields.includes(field)
      ? Number(payload[field])
      : payload[field];
  };

  const _getType = ({ field }) => {
    if (
      multiFilterStringFields.includes(field) ||
      multiFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }

    return FILTER_RULE_ENUMS.ComparableRuleType;
  };

  const value = [
    ...Object.keys(fields).map((field) => ({
      field: fieldsMap[field] || field,
      operator: FILTER_OPERATOR_ENUMS['='],
      type: _getType({ field }),
      value: _getValue({ field }),
    })),
  ];

  if (dateFrom && dateTo) {
    const startDate = new Date(dateFrom);

    const endDate = new Date(dateTo);

    const dateRule = {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value: [
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: fieldsMap.dateFrom || 'dateFrom',
          operator: FILTER_OPERATOR_ENUMS['>='],
          value: startDate.getTime(),
        },
        {
          type: FILTER_RULE_ENUMS.CompareDateRuleType,
          field: fieldsMap.dateTo || 'dateTo',
          operator: FILTER_OPERATOR_ENUMS['<='],
          value: endDate.getTime(),
        },
      ],
    };
    value.push(dateRule);
  }

  if (value.length) {
    return {
      type: FILTER_RULE_ENUMS.AndRuleType,
      field: null,
      operator: null,
      value,
    };
  }

  return {
    type: FILTER_RULE_ENUMS.EmptyRuleType,
  };
};

const handleComments = (data = []) =>
  data.map((item) => {
    const date = item.editedAt || item.createdAt;

    return {
      ...item,
      label: 'comment',
      name: item.author,
      shortName: item.author?.slice(0, 2)?.toUpperCase(),
      date: date ? formatDate(date, 'dd MMMM yyyy') : '-',
      time: date ? formatDate(date, 'HH:mm') : '-',
    };
  });

const getChangelogValue = (value) => {
  let newValue = value;
  if (Array.isArray(value) && !isEmpty(value)) {
    newValue = value.map((toItem) => `[${toItem.Key}: ${toItem.Value}] `);
  } else if (Array.isArray(value) && isEmpty(value)) {
    newValue = '[ ]';
  }
  return newValue || null;
};

const splitCamelCase = (str = '') => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
};

const handleChangelog = (data = []) =>
  data.map((item) => ({
    ...item,
    fieldName: splitCamelCase(last(item.field)),
    action: CHANGELOG_ACTIONS_MAP[item.action] || item.action,
    createdBy: item.actor,
    oldValue: getChangelogValue(item.from),
    newValue: getChangelogValue(item.to),
    shortName: item.actor?.slice(0, 2)?.toUpperCase(),
    date: item?.createdAt
      ? `${formatDate(item.createdAt, 'MMM d, yyyy')} at ${formatDate(
          item?.createdAt,
          'HH:mm'
        )}`
      : '',
  }));

const getPatchPayload = (values = {}) => {
  return {
    // vehicle overviews
    vin: values.vehicleForSale?.vehicleDetails?.vin,
    plate: values.vehicleForSale?.vehicleDetails?.plate,
    manufacturer: values.vehicleForSale?.vehicleDetails?.manufacturer,
    edition: values.vehicleForSale?.vehicleDetails?.edition,
    year: values.vehicleForSale?.vehicleDetails?.year
      ? Number(values.vehicleForSale?.vehicleDetails?.year)
      : 0,
    model: values.vehicleForSale?.vehicleDetails?.model,
    numberOfDoors: values.vehicleForSale?.vehicleDetails?.numberOfDoors
      ? Number(values.vehicleForSale?.vehicleDetails?.numberOfDoors)
      : 0,
    displacement: values.vehicleForSale?.vehicleDetails?.displacement
      ? Number(values.vehicleForSale?.vehicleDetails?.displacement)
      : 0,
    transmission: values.vehicleForSale?.vehicleDetails?.transmission,
    mileage: values.vehicleForSale?.vehicleDetails?.mileage?.value
      ? Number(values.vehicleForSale?.vehicleDetails?.mileage?.value)
      : 0,
    // vehicle restrictions and legal status
    structuralDamages:
      values.vehicleForSale?.bindingInformation?.structuralDamages,
    secondKey: values.vehicleForSale?.bindingInformation?.secondKey,
    passedKteo: values.vehicleForSale?.bindingInformation?.passedKteo,
    legalIssues: values.vehicleForSale?.bindingInformation?.legalIssues,
    serviceHistory: values.vehicleForSale?.bindingInformation?.serviceHistory,
    // qualitative assessment
    exteriorCondition:
      values.vehicleForSale?.qualityCharacteristics?.exteriorCondition || '',
    mechanicalCondition:
      values.vehicleForSale?.qualityCharacteristics?.mechanicalCondition || '',
    tireCondition:
      values.vehicleForSale?.qualityCharacteristics?.tireCondition || '',
    // dealership and appointment
    dealerName: values.dealer?.name,
    dealerEmail: values.dealer?.email,
    dealerCity: values.dealer?.city,
    dealerStreetName: values.dealer?.streetName,
    dealerStreetNumber: values.dealer?.streetNumber,
    dealerPostalCode: values.dealer?.postalCode,
    appointments: (values?.appointments || []).map(
      (appointment) => appointment.date
    ),
    transferOfOwnership: values.sourcing?.transferOfOwnership,
    registrationCertificate: values.sourcing?.registrationCertificate,
    kteo: values.sourcing?.kteo,
    serviceBook: values.sourcing?.serviceBook,
    taxDebtsCertificate: values.sourcing?.taxDebtsCertificate,
    sourcingSecondKey: values.sourcing?.secondKey,
    sellerTransferAuthorization:
      values.sourcing?.sellerTransferAuthorization || false,
    declarationOfNoLegalIssues:
      values.sourcing?.declarationOfNoLegalIssues || false,
    depositVerification: values.finance?.depositVerification,
    purchaseInvoice: values.finance?.purchaseInvoice,
    saleInvoice: values.finance?.saleInvoice,
    note: values.note || '',
  };
};

const handlePricing = (data = []) => {
  return data.map((item) => ({
    ...item,
    amount: item.amount.number,
  }));
};

const canAddCustomerPreferredPriceExists = (data, pricing = []) => {
  const askingPrice = pricing.find(
    (item) => item.name === CUSTOMER_PREFERRED_PRICE
  );
  if (!askingPrice) {
    return true;
  }
  if (askingPrice && trim(data.name) === CUSTOMER_PREFERRED_PRICE) {
    return false;
  }
  return true;
};
const canUpdatePrice = (data, pricing = []) => {
  if (trim(data.name) !== CUSTOMER_PREFERRED_PRICE) {
    return true;
  }
  const askingPrice = pricing.find(
    (item) => item.name === CUSTOMER_PREFERRED_PRICE
  );
  if (askingPrice?.id === data.id) {
    return true;
  }
  if (
    askingPrice?.id !== data.id &&
    trim(data.name) === CUSTOMER_PREFERRED_PRICE
  ) {
    return false;
  }

  return true;
};

const getParamsAndPayloadTradeTickets = ({
  page,
  pageSize,
  query,
  stage,
  contractPeriod,
  dateFrom,
  dateTo,
  orderBy,
  order,
  hasRequiredFiles,
  assignee,
}) => {
  const cleanedParams = getCleanedUrlParams({
    page,
    pageSize,
    query,
    orderBy,
    order,
    hasRequiredFiles,
  });

  const forPayloadParams = getCleanedUrlParams({
    stage,
    contractPeriod,
    assignee,
    dateFrom,
    dateTo,
  });

  const queryParams = getTradeTicketsQueryParams(cleanedParams);

  const payload = getTradeTicketsPayload(forPayloadParams);

  return { cleanedParams, queryParams, payload };
};

export {
  handleTradeTicketsData,
  getTradeTicketsQueryParams,
  getTradeTicketsColumns,
  handleComments,
  handleChangelog,
  getPatchPayload,
  handlePricing,
  canAddCustomerPreferredPriceExists,
  canUpdatePrice,
  getParamsAndPayloadTradeTickets,
};
